import React, { useState } from 'react';
import { Box, TableRow } from '@material-ui/core';
import { useHistory, useLocation } from 'react-router-dom';
import { isEmpty, isNumber, orderBy } from 'lodash/fp';
import { useTranslation } from 'react-i18next';
import {
    InventoryCountsQuery,
    WarehousePermission,
    useCreateInventoryCountMutation,
    useGetInitInfoQuery,
} from '../../../../generated/graphql';
import { useWarehousesScope } from '../../providers/WarehouseScopeProvider';
import { usePriceService } from '../../../../utils/hooks/priceService';
import { HoverLink, NoContent, Table, TableCell, Typography, WarehousePageWrapper } from '../../../../ui-kit';
import { routePaths } from '../../../../routePaths';
import { useDateTimeService } from '../../../../utils/hooks/dateTimeService';
import { useUser } from '../../../../utils/hooks/auth';
import { INVENTORY_COUNTS_QUERY } from '../../graphql/queries';
import { checkAccess } from '../../../../utils';
import { InventoryCountsRow, inventoryCountsHeadCells } from './utils';
import RealValuationCell from './RealValuationCell';

interface InventroyCountsTableProps {
    inventoryCounts: InventoryCountsQuery['inventoryCounts'];
}

const InventoryCountsTable = ({ inventoryCounts }: InventroyCountsTableProps) => {
    const startedCount = inventoryCounts.find(({ endDate }) => endDate == null);
    const [hasOpenedCounts, setOpenedCounts] = useState(!!startedCount);
    const { data } = useGetInitInfoQuery({ fetchPolicy: 'cache-only' });

    const { t } = useTranslation();
    const history = useHistory();
    const { currentWarehouseId } = useWarehousesScope();
    const inventoryCountButtonName = hasOpenedCounts ? t('Proceed to open count') : t('Start inventory count');

    const [runMutation] = useCreateInventoryCountMutation({
        variables: { warehouseId: currentWarehouseId as string },
        refetchQueries: [{ query: INVENTORY_COUNTS_QUERY, variables: { warehouseId: currentWarehouseId } }]
    });

    const startInventoryCount = async () => {
        await runMutation();
        setOpenedCounts(true);
        history.push(routePaths.currentInventoryCount);
    };

    const { priceService } = usePriceService();
    const { dateTimeService } = useDateTimeService();
    const { user: { cultureCode, defaultCurrency } } = useUser();
    const location = useLocation();

    const permissions = {
        warehouse: [hasOpenedCounts
            ? WarehousePermission.CanUpdateInventoryCounts
            : WarehousePermission.CanStartInventoryCount
        ]
    };

    if (isEmpty(inventoryCounts)) {
        return (
            <WarehousePageWrapper
                permissions={permissions}
                actions={[
                    {
                        isRouteExplicit: true,
                        route: routePaths.currentInventoryCount,
                        text: inventoryCountButtonName,
                        onClick: hasOpenedCounts ? undefined : startInventoryCount,
                    },
                ]}
            >
                <NoContent title={t('You have not started any inventory counts in this warehouse')} />
            </WarehousePageWrapper>
        );
    }
    const orderedCounts = orderBy('startDate', 'desc', inventoryCounts);

    const rows = orderedCounts.map(({ id, startDate, expectedValuation, realValuation, endDate, shortageSurplus, deltas }) => {
        let finalRealValuation;
        let finalEndDate;
        if (endDate && isNumber(realValuation)) {
            finalRealValuation = priceService.toLocaleString(realValuation, defaultCurrency.currencyCode);
            finalEndDate = dateTimeService.localizeShortDateTime(endDate, cultureCode);
        }

        return ({
            id,
            startDate: dateTimeService.localizeShortDateTime(startDate, cultureCode),
            expectedValuation: priceService.toLocaleString(expectedValuation || 0, defaultCurrency.currencyCode),
            realValuation: finalRealValuation,
            expectedValuationNumber: expectedValuation,
            realValuationNumber: realValuation,
            endDate: finalEndDate,
            shortage: priceService.toLocaleString(shortageSurplus.shortage.withoutVat, defaultCurrency.currencyCode),
            surplus: priceService.toLocaleString(shortageSurplus.surplus.withoutVat, defaultCurrency.currencyCode),
            delta: priceService.toLocaleString(deltas.withoutVat, defaultCurrency.currencyCode),
        });
    });

    const canViewExpectedValuation = data
        ? checkAccess({ warehouse: [WarehousePermission.CanViewInventoryCountExpectedValuation] }, data.employeePermissions)
        : false;
    const canViewRealValuation = data
        ? checkAccess({ warehouse: [WarehousePermission.CanViewInventoryCountRealValuation] }, data.employeePermissions)
        : false;


    const filteredInventoryCountsHeadCells = inventoryCountsHeadCells.filter(({ id }) => {
        if (id === 'expectedValuation') {
            return canViewExpectedValuation;
        }
        if (id === 'realValuation' || id === 'shortage' || id === 'surplus' || id === 'delta') {
            return canViewRealValuation;
        }
        return true;
    });

    const tableContent = rows.map(row => (
        <TableRow tabIndex={-1} key={row.id}>
            {filteredInventoryCountsHeadCells.map(({ id, align }) => {
                const isFirstElement = id === 'startDate';
                let cellValue: string | JSX.Element | undefined = row[id];
                if (id === 'realValuation' && row.endDate && row.realValuation) {
                    const { realValuationNumber, expectedValuationNumber } = row;
                    if (isNumber(realValuationNumber) && isNumber(expectedValuationNumber)) {
                        if (realValuationNumber > expectedValuationNumber) {
                            cellValue = (<RealValuationCell variant="success">{cellValue}</RealValuationCell>);
                        }

                        if (realValuationNumber < expectedValuationNumber) {
                            cellValue = <RealValuationCell variant="error">{cellValue}</RealValuationCell>;
                        }
                    }
                }

                if (id === 'shortage') {
                    cellValue = <Typography variant='body2' colorVariant='error'>{cellValue}</Typography>;
                }

                if (id === 'surplus') {
                    cellValue = <Typography variant='body2' colorVariant='success'>{cellValue}</Typography>;
                }

                if ((id === 'realValuation' || id === 'endDate' || id === 'shortage' || id === 'surplus' || id === 'delta') &&
                !row.endDate && !row.realValuation) {
                    cellValue = t('Not finished');
                }

                const component = isFirstElement ? 'th' : 'td';

                return (
                    <TableCell
                        component={component}
                        isSticky={isFirstElement}
                        key={id} id={id} align={align}
                    >
                        {isFirstElement
                            ? (
                                <Typography variant="body2" fontWeight='medium'>
                                    <HoverLink
                                        wide
                                        to={
                                        {
                                            pathname:`${routePaths.inventoryCounts}/${row.id}`,
                                            state: { background: location },
                                        }
                                        }
                                    >
                                        {cellValue}
                                    </HoverLink>
                                </Typography>
                            )
                            : cellValue}
                    </TableCell>
                );
            })}
        </TableRow>
    ));
    return (
        <WarehousePageWrapper
            permissions={permissions}
            actions={[
                {
                    isRouteExplicit: true,
                    route: routePaths.currentInventoryCount,
                    text: inventoryCountButtonName,
                    onClick: hasOpenedCounts ? undefined : startInventoryCount,
                },
            ]}
        >
            <Box width="100%">
                <Table<InventoryCountsRow> headCells={filteredInventoryCountsHeadCells} isFirstColumnSticky>
                    {tableContent}
                </Table>
            </Box>
        </WarehousePageWrapper>
    );
};

export default InventoryCountsTable;
